import { cn } from "../utils";
import { MainContainer } from "../layout/MainContainer";
import type { HTMLAttributes } from "react";

export interface SectionShellProps extends HTMLAttributes<HTMLElement> {
  /** Optional max width for the content */
  maxWidth?: "lg" | "xl" | "2xl" | "full";
  /** Optional background class (e.g., gradient or surface) */
  backgroundClassName?: string;
  /** Optional class name for the outer section element */
  sectionClassName?: string;
  /** Optional surface treatment */
  surface?: "default" | "band";
  /** Override default vertical padding */
  paddingClassName?: string;
}

/**
 * Wrapper to enforce consistent padding and max-width for sections.
 * Provides a hook for background treatments (surfaces/gradients).
 */
export function SectionShell({
  className,
  children,
  maxWidth = "xl",
  backgroundClassName,
  sectionClassName,
  surface = "default",
  paddingClassName = "py-16 md:py-24",
  ...props
}: SectionShellProps) {
  const surfaceStyle =
    surface === "band"
      ? ({ backgroundImage: "var(--gradient-surface-band)" } as React.CSSProperties)
      : undefined;

  const backgroundStyle: React.CSSProperties = {};
  if (backgroundClassName?.includes("bg-surface-alt")) {
    backgroundStyle.backgroundColor = "var(--color-surface-alt)";
  } else if (backgroundClassName?.includes("bg-surface")) {
    backgroundStyle.backgroundColor = "var(--color-surface)";
  }

  return (
    <section
      className={cn(paddingClassName, backgroundClassName, sectionClassName)}
      style={{ ...surfaceStyle, ...backgroundStyle }}
      {...props}
    >
      <MainContainer maxWidth={maxWidth} className={cn(className)}>
        {children}
      </MainContainer>
    </section>
  );
}
